import { Entity, parseEntityRef } from '@backstage/catalog-model';

export type Env = 'dev' | 'stage' | 'prod';
export const ENVS: Env[] = ['dev', 'stage', 'prod'];

export function isDownstream(source: Env, target: Env): boolean {
  return ENVS.indexOf(target) > ENVS.indexOf(source);
}

export interface ServiceEntry {
  name: string;
  entity: Entity;
  versionUrls: Record<Env, string | undefined>;
}

const ANNOTATION_PREFIX = 'salestrak.io/version-url';

export function extractReleasableServices(entities: Entity[]): ServiceEntry[] {
  return entities
    .filter((e) => {
      const annotations = e.metadata.annotations ?? {};
      return ENVS.some((env) => annotations[`${ANNOTATION_PREFIX}-${env}`]);
    })
    .map((e) => {
      const annotations = e.metadata.annotations ?? {};
      const versionUrls: Record<Env, string | undefined> = {
        dev: annotations[`${ANNOTATION_PREFIX}-dev`],
        stage: annotations[`${ANNOTATION_PREFIX}-stage`],
        prod: annotations[`${ANNOTATION_PREFIX}-prod`],
      };
      return { name: e.metadata.name, entity: e, versionUrls };
    });
}

// Resolves deploy order for a selected set of services by topo-sorting over
// catalog `dependsOn` component relations. Services not in the selected set
// are ignored (non-component deps like resources/APIs are filtered out too).
export function resolveDepOrder(
  services: ServiceEntry[],
  selectedNames: Set<string>,
): ServiceEntry[] {
  const selected = services.filter((s) => selectedNames.has(s.name));
  const byName = new Map(selected.map((s) => [s.name, s]));

  const deps = (svc: ServiceEntry): string[] => {
    const rels = svc.entity.relations ?? [];
    return rels
      .filter((r) => r.type === 'dependsOn')
      .map((r) => {
        try {
          const parsed = parseEntityRef(r.targetRef);
          return parsed.kind === 'component' ? parsed.name : null;
        } catch {
          return null;
        }
      })
      .filter((n): n is string => !!n && byName.has(n));
  };

  const out: ServiceEntry[] = [];
  const seen = new Set<string>();
  const visiting = new Set<string>();

  const visit = (svc: ServiceEntry) => {
    if (seen.has(svc.name)) return;
    if (visiting.has(svc.name)) return; // cycle guard — skip
    visiting.add(svc.name);
    for (const depName of deps(svc)) {
      const dep = byName.get(depName);
      if (dep) visit(dep);
    }
    visiting.delete(svc.name);
    seen.add(svc.name);
    out.push(svc);
  };

  for (const s of selected) visit(s);
  return out;
}

// ─── Markdown aggregation ──────────────────────────────────────────

export interface ReleaseNoteRecord {
  service: string;
  version: string;
  releaseNotes: string;
}

function parseSections(markdown: string): Map<string, string[]> {
  const sections = new Map<string, string[]>();
  let currentSection = 'General';

  for (const line of markdown.split('\n')) {
    const headerMatch = line.match(/^##\s+(.+)/);
    if (headerMatch) {
      currentSection = headerMatch[1].trim();
      if (!sections.has(currentSection)) sections.set(currentSection, []);
    } else if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      if (!sections.has(currentSection)) sections.set(currentSection, []);
      sections.get(currentSection)!.push(line);
    } else if (line.trim() && currentSection !== 'General') {
      if (!sections.has(currentSection)) sections.set(currentSection, []);
      sections.get(currentSection)!.push(line);
    }
  }
  return sections;
}

const SECTION_ORDER = [
  'Breaking Changes',
  'BREAKING CHANGES',
  'Features',
  'New Features',
  'Enhancements',
  'Bug Fixes',
  'Performance Improvements',
  'Style Updates',
  'Documentation Updates',
];

// Merges per-service release notes into a single document grouped by section.
// Unique-dedupe within each section.
export function buildAggregatedMarkdown(
  perService: { service: string; notes: ReleaseNoteRecord[] }[],
): string {
  const merged = new Map<string, string[]>();

  for (const { notes } of perService) {
    for (const note of notes) {
      const sections = parseSections(note.releaseNotes);
      for (const [header, items] of sections) {
        if (!merged.has(header)) merged.set(header, []);
        merged.get(header)!.push(...items);
      }
    }
  }

  if (merged.size === 0) return 'No release notes found for the selected range.';

  const used = new Set<string>();
  const lines: string[] = [];

  for (const key of SECTION_ORDER) {
    for (const [header, items] of merged) {
      if (header.toLowerCase() === key.toLowerCase() && items.length > 0) {
        lines.push(`## ${header}`);
        lines.push(...Array.from(new Set(items)));
        lines.push('');
        used.add(header);
      }
    }
  }
  for (const [header, items] of merged) {
    if (!used.has(header) && items.length > 0) {
      lines.push(`## ${header}`);
      lines.push(...Array.from(new Set(items)));
      lines.push('');
    }
  }
  return lines.join('\n').trim();
}

const GEMINI_PROMPT = `You are a product writer turning raw, aggregated engineering release notes from multiple software services into a single polished, customer-facing changelog.

Do all of the following:
- Remove near-duplicate items — items that describe the same change in different words across services. Merge them into one item.
- Group related items that touch the same feature or area into a single, higher-level item. For example, "updated entity selector in issue view" and "fixed padding for issue view properties" should become one item like "Issue view UX enhancements". Favor a concise summary over listing every small change in an area.
- Drop negligible changes — minor tweaks with little or no meaningful customer impact (small style/padding/copy tweaks, tiny adjustments) when they aren't worth calling out on their own. Roll them into a broader area item per the grouping rule, or omit them entirely if there's nothing substantive to report.
- Rewrite every item in clear, customer-facing language. Drop internal jargon: conventional-commit prefixes (feat:, fix:, chore:, etc.), ticket/issue IDs (e.g. JIRA-123, #456), commit hashes, branch names, and internal service or repo names. Describe the user-visible change or benefit, not the implementation.
- Keep items concise (one line each) and written in a consistent, neutral product voice.
- Omit items that are purely internal and have no customer impact (e.g. dependency bumps, CI/build tweaks, refactors, test changes) unless they clearly affect users.

Preserve the ## section headers (you may rename them to friendlier customer-facing equivalents, e.g. "Bug Fixes" → "Fixes", but keep them as ## headers). Do not invent changes that aren't in the input. Do not add preamble, commentary, or explanation. Return only the cleaned markdown.`;

// Aggregates per-service notes into a single markdown document, then sends it
// through Gemini (via Backstage proxy) to remove near-duplicate line items and
// rewrite them into polished, customer-facing verbiage.
// Falls back to the raw aggregated markdown if the Gemini call fails.
export async function aggregateAndClean(
  perService: { service: string; notes: ReleaseNoteRecord[] }[],
  backstageFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
  backendUrl: string,
): Promise<string> {
  const raw = buildAggregatedMarkdown(perService);
  if (raw === 'No release notes found for the selected range.') return raw;

  try {
    const res = await backstageFetch(
      `${backendUrl}/api/proxy/gemini/v1beta/models/gemini-2.5-flash:generateContent`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: `${GEMINI_PROMPT}\n\n${raw}` }] }],
        }),
      },
    );
    if (!res.ok) return raw;
    const data = await res.json();
    return data?.candidates?.[0]?.content?.parts?.[0]?.text ?? raw;
  } catch {
    return raw;
  }
}
